/**
 * client/camera/viewmove.ts — camera effects that are state machines over time (pure, no THREE).
 *
 * OWNER: VIEWMODEL+CAMERA agent. All ports of T5 (KisakBlack) code — ESTIMATE for T6 where noted in dvars.ts.
 *  - ViewKick      : BG_WeaponFireRecoil (bgame/bg_weapons.cpp:5014) view part + CG_KickAngles (cgame_mp/cg_view_mp.cpp:4532)
 *  - CameraShakes  : CG_StartShakeCamera / CG_UpdateCameraShake / CG_ShakeCamera (cgame/cg_camerashake.cpp)
 *  - DamageFlinch  : P_DamageFeedback (game_mp/g_active_mp.cpp) + BG_CalculateViewMovement_DamageKick and
 *                    BG_ComputeAndApplyWeaponMovement_DamageKickAngles (bgame/bg_weapons_view.cpp)
 *  - LandDip       : CG_OffsetFirstPersonView landChange (cgame_mp/cg_view_mp.cpp) + cg_event.cpp:1915-1940
 *  - StepSmoother  : CG_SmoothCameraZ + CG_PredictPlayerState stepViewChange (cgame_mp/cg_predict_mp.cpp:693-728)
 * Times are client presentation ms (the camera's own clock), never simulation time.
 */
import type { Vec3 } from '@shared/math';
import {
  BG_VIEW_KICK_MAX, BG_VIEW_KICK_MIN, BG_VIEW_KICK_SCALE, CG_VIEW_Z_SMOOTHING_MAX, CG_VIEW_Z_SMOOTHING_MIN,
  CG_VIEW_Z_SMOOTHING_TIME_MS, DAMAGE_KICK_DEFLECT_MS, DAMAGE_KICK_RETURN_MS, KICK_CENTER_SPEED_NOWEAPON, KICK_MAX_ANGLE,
  KICK_RETURN_DAMP, KICK_STEP_MS, LAND_DEFLECT_MS, LAND_GUN_SCALE, LAND_RETURN_MS, LAND_VIEWDIP_MAX, SHAKE_AMP_DEG,
  SHAKE_FREQ, SHAKE_PERIOD_DIV_MS, SHAKE_SLOTS,
} from './dvars';

/** universal/q_shared.cpp GetLeanFraction: ease (2-|f|)*f. */
export function getLeanFraction(f: number): number {
  return (2 - Math.abs(f)) * f;
}

// =============================================================================================
// View kick (fire)
// =============================================================================================
export interface ViewKickDef {
  hipViewKickPitchMin: number; hipViewKickPitchMax: number; hipViewKickYawMin: number; hipViewKickYawMax: number;
  adsViewKickPitchMin: number; adsViewKickPitchMax: number; adsViewKickYawMin: number; adsViewKickYawMax: number;
  hipViewKickCenterSpeed: number; adsViewKickCenterSpeed: number;
}

export class ViewKick {
  /** cg.kickAngles [pitch, yaw, roll], degrees. */
  readonly angles: Vec3 = [0, 0, 0];
  /** cg.kickAVel, degrees/s. */
  readonly avel: Vec3 = [0, 0, 0];

  /** BG_WeaponFireRecoil (view part). r1/r2 in [0,1) (bgs->Random()). */
  fire(def: ViewKickDef, adsFrac: number, r1: number, r2: number): void {
    let pitch: number, yaw: number;
    if (adsFrac !== 1) {
      pitch = r1 * (def.hipViewKickPitchMax - def.hipViewKickPitchMin) + def.hipViewKickPitchMin;
      yaw = r2 * (def.hipViewKickYawMax - def.hipViewKickYawMin) + def.hipViewKickYawMin;
    } else {
      pitch = r1 * (def.adsViewKickPitchMax - def.adsViewKickPitchMin) + def.adsViewKickPitchMin;
      yaw = r2 * (def.adsViewKickYawMax - def.adsViewKickYawMin) + def.adsViewKickYawMin;
    }
    this.avel[0] = -pitch;
    this.avel[1] = yaw;
    this.avel[2] = -0.5 * yaw;
  }

  /** CG_KickAngles: integrate in 5 ms steps. def null = no weapon (centre speed 2400). */
  update(frametimeMs: number, def: ViewKickDef | null, adsFrac: number): void {
    const a = this.angles, v = this.avel;
    for (let t = frametimeMs; t > 0; t -= KICK_STEP_MS) {
      const ft = (t <= KICK_STEP_MS ? t : KICK_STEP_MS) * 0.001;
      for (let i = 0; i < 3; i++) {
        if (v[i] === 0 && a[i] === 0) continue;
        if (a[i] !== 0) {
          const dir = a[i] <= 0 ? 1 : -1;
          const center = def ? (adsFrac <= 0.5 ? def.hipViewKickCenterSpeed : def.adsViewKickCenterSpeed) : KICK_CENTER_SPEED_NOWEAPON;
          v[i] += dir * center * ft;
        }
        let change = v[i] * ft;
        if (a[i] * change < 0) change *= KICK_RETURN_DAMP;
        if ((a[i] + change) * a[i] < 0) {
          a[i] = 0; v[i] = 0;
        } else {
          a[i] += change;
          if (a[i] === 0) v[i] = 0;
          else if (Math.abs(a[i]) > KICK_MAX_ANGLE) { a[i] = a[i] <= 0 ? -KICK_MAX_ANGLE : KICK_MAX_ANGLE; v[i] = 0; }
        }
      }
    }
  }

  reset(): void {
    this.angles[0] = this.angles[1] = this.angles[2] = 0;
    this.avel[0] = this.avel[1] = this.avel[2] = 0;
  }
}

// =============================================================================================
// Camera shake (explosions)
// =============================================================================================
interface Shake { scale: number; length: number; time: number; src: Vec3; radius: number; size: number; rumbleScale: number }

export class CameraShakes {
  private readonly shakes: Shake[] = [];
  private phase = 0;

  constructor(private readonly rand: () => number = Math.random) {
    for (let i = 0; i < SHAKE_SLOTS; i++) this.shakes.push({ scale: 0, length: 0, time: 0, src: [0, 0, 0], radius: 0, size: 0, rumbleScale: 0 });
  }

  /** CG_UpdateCameraShake: returns false when inactive; fills size/rumbleScale. */
  private updateShake(s: Shake, now: number, eye: Readonly<Vec3>): boolean {
    const dtime = now - s.time;
    if (dtime < 0 || dtime >= s.length || s.length <= 0 || s.radius <= 0 || s.scale <= 0) return false;
    const dist = Math.hypot(s.src[0] - eye[0], s.src[1] - eye[1], s.src[2] - eye[2]);
    const val = 1 - dist / s.radius;
    const x = (1 - dtime / s.length) * s.scale;
    if (x <= 0) return false;
    s.size = val < 0 ? val / x : val * x;
    s.rumbleScale = x;
    return true;
  }

  /** CG_StartShakeCamera. */
  start(now: number, scale: number, durationMs: number, src: Readonly<Vec3>, radius: number, eye: Readonly<Vec3>): void {
    if (scale <= 0 || durationMs <= 0 || radius <= 0) return;
    const b: Shake = { scale, length: durationMs, time: now, src: [src[0], src[1], src[2]], radius, size: 0, rumbleScale: 0 };
    this.updateShake(b, now, eye);
    let slot = -1;
    for (let i = 0; i < SHAKE_SLOTS; i++) {
      const s = this.shakes[i];
      if (s.time > now || now >= s.time + s.length) { slot = i; break; }
    }
    if (slot < 0) {
      let minsize = b.size;
      for (let j = 0; j < SHAKE_SLOTS; j++) if (minsize > this.shakes[j].size) { minsize = this.shakes[j].size; slot = j; }
    }
    if (slot >= 0) this.shakes[slot] = b;
  }

  /** CG_ShakeCamera: add the shake to the view angles. Returns the strongest shake scale (0 = none). */
  apply(now: number, eye: Readonly<Vec3>, angles: Vec3): number {
    let scale = 0, rumble = 0;
    for (const s of this.shakes) {
      if (this.updateShake(s, now, eye) && s.size > scale) { scale = s.size; rumble = s.rumbleScale; }
    }
    if (scale > 0) {
      if (scale > 1) scale = 1;
      const sx = now / SHAKE_PERIOD_DIV_MS;
      angles[0] += Math.sin(SHAKE_FREQ[0] * sx + this.phase) * rumble * SHAKE_AMP_DEG[0] * scale;
      angles[1] += Math.sin(SHAKE_FREQ[1] * sx + this.phase) * rumble * SHAKE_AMP_DEG[1] * scale;
      angles[2] += Math.sin(SHAKE_FREQ[2] * sx + this.phase) * rumble * SHAKE_AMP_DEG[2] * scale;
    } else {
      this.phase = (this.rand() * 2 - 1) * Math.PI; // crandom() * PI
    }
    return scale;
  }

  clear(): void { for (const s of this.shakes) { s.length = 0; s.scale = 0; } }
}

// =============================================================================================
// Damage flinch
// =============================================================================================
export class DamageFlinch {
  /** cg.damageTime (presentation ms), 0 = none. */
  time = 0;
  pitch = 0;
  roll = 0;

  /**
   * P_DamageFeedback / CG_DamageFeedback. `dir` = direction the damage travelled (attacker -> victim), or null
   * for world damage (falling, trigger_hurt). viewFwd/viewRight = refdef axes (right = -axis[1]).
   */
  hit(now: number, damage: number, maxHealth: number, dir: Readonly<Vec3> | null, viewFwd: Readonly<Vec3>, viewRight: Readonly<Vec3>): void {
    let d = maxHealth > 0 ? Math.trunc((100 * damage) / maxHealth) : damage;
    if (d > 127) d = 127;
    if (d <= 0) return;
    let kick = d * BG_VIEW_KICK_SCALE;
    if (kick < BG_VIEW_KICK_MIN) kick = BG_VIEW_KICK_MIN;
    else if (kick > BG_VIEW_KICK_MAX) kick = BG_VIEW_KICK_MAX;
    if (!dir) {
      this.roll = 0;
      this.pitch = -kick;
    } else {
      // engine: v_dmg_roll = -kick * dot(dir, axis[1] = left) ; v_dmg_pitch = kick * dot(dir, forward)
      const left = -(dir[0] * viewRight[0] + dir[1] * viewRight[1] + dir[2] * viewRight[2]);
      this.roll = -kick * left;
      this.pitch = kick * (dir[0] * viewFwd[0] + dir[1] * viewFwd[1] + dir[2] * viewFwd[2]);
    }
    this.time = now;
  }

  /** BG_CalculateViewMovement_DamageKick: adds to view angles. */
  applyView(now: number, adsFrac: number, angles: Vec3): void {
    if (!this.time) return;
    const factor = 1 - adsFrac * 0.5; // overlayReticle is 'none' for every SnS weapon
    const t = now - this.time;
    let r: number;
    if (t >= DAMAGE_KICK_DEFLECT_MS) {
      const ra = 1 - (t - DAMAGE_KICK_DEFLECT_MS) / DAMAGE_KICK_RETURN_MS;
      if (ra <= 0) return;
      r = (1 - getLeanFraction(1 - ra)) * factor;
    } else {
      r = getLeanFraction(t / DAMAGE_KICK_DEFLECT_MS) * factor;
    }
    angles[0] += r * this.pitch;
    angles[2] += r * this.roll;
  }

  /** BG_ComputeAndApplyWeaponMovement_DamageKickAngles: adds to the gun angles. */
  applyWeapon(now: number, adsFrac: number, angles: Vec3): void {
    if (!this.time) return;
    const factor = adsFrac * 0.5 + 0.5;
    const deflect = DAMAGE_KICK_DEFLECT_MS * factor, ret = DAMAGE_KICK_RETURN_MS * factor;
    const t = now - this.time;
    let r: number;
    if (deflect <= t) {
      const ra = 1 - (t - deflect) / ret;
      if (ra <= 0) return;
      r = (1 - getLeanFraction(1 - ra)) * factor;
    } else {
      r = getLeanFraction(t / deflect) * factor;
    }
    angles[0] += r * this.pitch * 0.5;
    angles[1] -= r * this.roll;
    angles[2] += r * this.roll * 0.5;
  }
}

// =============================================================================================
// Landing dip
// =============================================================================================
/** PM_CrashLand viewDip from a fall height (T5 bg_pmove.cpp:3554 / cg_event.cpp:1918). */
export function landViewDip(fallHeight: number): number {
  let dip = fallHeight > 12 ? Math.trunc(((fallHeight - 12) / 26) * 4 + 4) : 0;
  if (dip > LAND_VIEWDIP_MAX) dip = LAND_VIEWDIP_MAX;
  return dip;
}

export class LandDip {
  change = 0;
  time = -1e9;

  land(now: number, viewDip: number): void {
    if (viewDip <= 0) return;
    this.change = -viewDip;
    this.time = now;
  }

  /** CG_OffsetFirstPersonView: eye z offset. */
  view(now: number): number {
    const d = now - this.time;
    if (d > 0 && d < LAND_DEFLECT_MS) return this.change * (d / LAND_DEFLECT_MS);
    if (d > 0 && d < LAND_DEFLECT_MS + LAND_RETURN_MS) return this.change * (1 - (d - LAND_DEFLECT_MS) / LAND_RETURN_MS);
    return 0;
  }

  /** CG_CalculateWeaponMovement_Landing: gun z offset (0.25x, integer-ms formula). */
  gun(now: number): number {
    const d = Math.trunc(now - this.time);
    if (d < 0) return 0;
    if (d < LAND_DEFLECT_MS) return (this.change * LAND_GUN_SCALE * d) / LAND_DEFLECT_MS;
    if (d < LAND_DEFLECT_MS + LAND_RETURN_MS) return (this.change * LAND_GUN_SCALE * (LAND_DEFLECT_MS + LAND_RETURN_MS - d)) / LAND_RETURN_MS;
    return 0;
  }
}

// =============================================================================================
// Step smoothing
// =============================================================================================
export class StepSmoother {
  change = 0;
  start = -1e9;

  /** cg_predict_mp.cpp: accumulate a signed step (pm->viewChange). */
  step(now: number, diff: number): void {
    if (Math.abs(diff) < CG_VIEW_Z_SMOOTHING_MIN) return;
    let remaining = 0;
    const since = now - this.start;
    if (since >= 0 && since < CG_VIEW_Z_SMOOTHING_TIME_MS) remaining = (1 - since / CG_VIEW_Z_SMOOTHING_TIME_MS) * this.change;
    const v = remaining + diff;
    this.change = v <= 0 ? Math.max(v, -CG_VIEW_Z_SMOOTHING_MAX) : Math.min(v, CG_VIEW_Z_SMOOTHING_MAX);
    this.start = now;
  }

  /** CG_SmoothCameraZ: value to ADD to the eye z. */
  offset(now: number): number {
    if (this.change === 0) return 0;
    const t = now - this.start;
    if (t < 0) return 0;
    const lerp = t < CG_VIEW_Z_SMOOTHING_TIME_MS ? t / CG_VIEW_Z_SMOOTHING_TIME_MS : 1;
    if (lerp >= 1) { this.change = 0; return 0; }
    return -(1 - lerp) * this.change;
  }

  reset(): void { this.change = 0; this.start = -1e9; }
}
